import cn from 'classnames';
import JSConfetti from 'js-confetti';
import { useState, useContext, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import Button from 'components/common/Button';
import Text from 'components/common/Text';
import DiscountTag from 'components/icons/illustrative/DiscountTag';
import { ProgressBarContext, VoucherContext, UserContext } from 'components/signUpFunnel/Contexts';
import IdMeForm from 'components/signUpFunnel/idMe/IdMeForm';
import FunnelHeader from 'components/signUpFunnel/shared/FunnelHeader';
import { BrandContext, HOME_CHEF } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import localStorage from 'helpers/localStorage';
import { getBrandRoute } from 'helpers/routes';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useAssignExperiment from 'hooks/useAssignExperiment';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useMetadata from 'hooks/useMetadata';
import { FAMILY, SHOW_PROMO_BANNER_LOCAL_STORAGE, Statuses, HEROES_REFERRER } from 'lib/constants';
import { TEMPO_IDME_OFFER } from 'lib/constants/experimentConstants';
import { ICON_SIZES } from 'lib/constants/iconSizes';
import { hideIdMe } from 'lib/constants/idme';
import * as ErrorLogging from 'services/ErrorLogging';
import { idMeVerification } from 'services/RequestService/endpoints/idMeVerification';
import { addVoucher, createEvents } from 'services/RequestService/endpoints/signUp';
import { updateStatus } from 'services/RequestService/endpoints/user';
import styles from 'styles/components/signUpFunnel/promo/PromoCodeSection';

import PromoApplied from './PromoApplied';
import PromoCodeSection from './PromoCodeSection';
import PromoSkeleton from './PromoSkeleton';

const PromoContainer = () => {
  const [hasPromo, setHasPromo] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [showIdMeError, setShowIdMeError] = useState(false);
  const { setProgress } = useContext(ProgressBarContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, setUser } = useContext(UserContext);
  const { voucher, setVoucher, getVoucher } = useContext(VoucherContext);
  const { brand } = useContext(BrandContext);
  const [hasIdMe, setHasIdMe] = useState(false);
  const mealPlanSlug = useFunnelMealPlan();
  const isFamilyMealPlan = mealPlanSlug === FAMILY;

  const transitionRef = useRef();
  const brandRoutes = getBrandRoute({ brand: brand, isFamily: isFamilyMealPlan });

  const { hash } = useLocation();
  const queryParams = new URLSearchParams(hash.substring(1));
  const accessToken = queryParams.get('access_token');

  let jsConfetti;

  const history = useHistory();

  const isHeroesReferer = localStorage.read(HEROES_REFERRER);

  const { isVariant: isTempoIdMeOfferVariant } = useAssignExperiment({
    name: TEMPO_IDME_OFFER,
    excludedBrand: HOME_CHEF,
    additionalConditions: [!isHeroesReferer],
  });

  useMetadata({
    title: `Step 3: Promo | ${brandData(brand)?.displayName}`,
  });

  useEffect(() => {
    setProgress({ percent: '40' });

    createEvents({ params: { event_name: 'PageView' } }).catch(error =>
      ErrorLogging.captureException(error.message)
    );
  }, []);

  useEffect(() => {
    getVoucher();
  }, []);

  useEffect(() => {
    setHasIdMe(user?.idMeData?.idMeUser);
  }, [user?.idMeData?.idMeUser]);

  useEffect(() => {
    jsConfetti = new JSConfetti();
  }, []);

  useEffect(() => {
    if (user?.isAmex || user?.isUsaa || (hasIdMe && user?.signupVoucher)) {
      setHasPromo(true);
      if (jsConfetti) jsConfetti.addConfetti();
    } else if (voucher) {
      setHasPromo(true);
    }

    setIsSubmitting(false);
  }, [voucher, user, hasIdMe, jsConfetti]);

  useEffect(() => {
    const verifyIdMe = async () => {
      try {
        const response = await idMeVerification({ params: { token: accessToken } });

        if (response.ok && response.verified) {
          if (response.signupVoucher && response.signupVoucher.isIdMeMilitaryHealthTeacher) {
            setHasIdMe(true);
          }
          setVoucher({ ...response.signupVoucher });
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
        setShowIdMeError(true);
      }
    };
    if (accessToken) {
      verifyIdMe();
    }
  }, []);

  useAnalyticsEffect(
    ({ logEvent, events }) => {
      if (brand) {
        logEvent(events.signUp[brand].promo.newSignUpPromoViewed);
      }
    },
    [brand]
  );

  const handlePromoSubmitClickWithAnalytics = useAnalyticsCallback(
    () => {
      updateStatus({
        params: {
          status: Statuses.BOX_PEOPLE_STEP,
        },
      }).then(response => {
        if (response.ok) {
          setUser(prevUser => ({
            ...prevUser,
            ...response.user,
          }));
        }
        history.push(brandRoutes.boxRecommenderPeople);
      });
    },
    ({ logEvent, events }) => {
      if (brand) {
        logEvent(events.signUp[brand].promo.newSignUpPromoSubmitted, {
          mealPlanSlug: mealPlanSlug,
        });
      }
    },
    [brand, mealPlanSlug, brandRoutes]
  );

  const handleApplyButtonClickWithAnalytics = useAnalyticsCallback(
    async params => {
      setIsSubmitting(true);
      const response = await addVoucher({ params });
      if (response.ok) {
        setVoucher(response.signupVoucher);
        setHasPromo(true);
        if (!response.signupVoucher.isCourtesyVoucher) {
          if (jsConfetti) jsConfetti.addConfetti();
        }
      } else {
        setHasPromo(false);
        setErrorMessage(response.error);
      }

      setIsSubmitting(false);
      localStorage.delete(SHOW_PROMO_BANNER_LOCAL_STORAGE);
    },
    ({ logEvent, events }) => {
      if (brand) {
        logEvent(events.signUp[brand].promo.newSignUpPromoApplied);
      }
    },
    [brand, setIsSubmitting, addVoucher, setVoucher, setHasPromo, setErrorMessage, jsConfetti]
  );

  const handleSecretSaleClickWithAnalytics = useAnalyticsCallback(
    () => {
      setHasPromo(true);
      setVoucher({ discounts: [] });
      if (jsConfetti) jsConfetti.addConfetti();
    },
    ({ logEvent, events }) => {
      if (brand) {
        logEvent(events.signUp[brand].promo.newSignUpSecretSaleClaimed);
      }
    },
    [brand, jsConfetti]
  );

  const PromoCodeApplied = () => (
    <>
      <FunnelHeader
        containerClassName="mb-6 mt-8 text-left"
        title={voucher?.headerText || 'Discount applied!'}
      />
      <PromoApplied
        setHasPromo={setHasPromo}
        setErrorMessage={setErrorMessage}
        isUsaa={user?.isUsaa}
        isAmexUser={user?.isAmex}
        hideChangePromoAction={user?.hideChangePromoAction}
        isCourtesyVoucher={voucher?.isCourtesyVoucher}
        containerStyles={cn('px-4 py-6 mb-6 mx-auto rounded-3xl', styles.background, styles[brand])}
        showHeading
        promoTextStyles="text-center"
      />
    </>
  );

  const PromoCodeNotApplied = () => (
    <>
      <FunnelHeader
        title="Would you like a promo code?"
        titleClassName="bp1:whitespace-nowrap"
        containerClassName="pb-4 pt-6 text-left bp2:pt-8"
      />
      <PromoCodeSection
        handleAddVoucherClick={handleApplyButtonClickWithAnalytics}
        handleSecretSaleClick={handleSecretSaleClickWithAnalytics}
        errorMessage={errorMessage}
        isSubmitting={isSubmitting}
      />
    </>
  );

  return (
    <>
      {user ? (
        <section className="mx-auto flex w-full flex-col px-6 bp1:w-[522px] bp1:px-0 bp2:pb-6">
          {hasPromo ? <PromoCodeApplied /> : <PromoCodeNotApplied />}
          {!hideIdMe(voucher, user) && (
            <div className="w-full">
              <div className="flex items-start gap-2">
                <DiscountTag svgSize={ICON_SIZES.SM} />
                <Text brandStylesOverrides="textMd" className="mb-0 text-left">
                  ID.me discounts available:
                </Text>
              </div>
              <IdMeForm
                showIdMeError={showIdMeError}
                transitionRef={transitionRef}
                brand={brand}
                isFamilyMealPlan={isFamilyMealPlan}
                isBilling={false}
                voucher={voucher}
                hasIdMe={hasIdMe}
                isTempoIdMeOfferVariant={isTempoIdMeOfferVariant}
              />
            </div>
          )}
          <div className="mt-6 w-full text-center">
            <Button
              type="button"
              className="btn w-full text-lg"
              onClick={handlePromoSubmitClickWithAnalytics}
              variation="Pill"
            >
              Continue
            </Button>
          </div>
        </section>
      ) : (
        <PromoSkeleton />
      )}
    </>
  );
};

export default PromoContainer;
